import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, RefreshCw, Eye, EyeOff, Zap, ShieldCheck, ChevronLeft } from "lucide-react";
import { tr } from "@/lib/i18n";

const MODES = ["test", "live"];
const PRICE_KEYS = [
  { id: "leo_single_scene", i18n: "Pojedyncza Scena" },
  { id: "leo_sprint_48h",   i18n: "Sprint 48h" },
  { id: "leo_monthly",      i18n: "Monthly" },
  { id: "leo_annual",       i18n: "Annual" },
  { id: "leo_light_pln",    i18n: "Light (PLN)" },
  { id: "leo_light_usd",    i18n: "Light (USD)" },
];

function SecretInput({ value, existingLast4, hasValue, onChange, placeholder, testid }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testid}
        type={reveal ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="script-input mono text-sm w-full pr-10"
        placeholder={hasValue ? existingLast4 : placeholder}
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        onClick={() => setReveal((r) => !r)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-200 p-1"
        tabIndex={-1}
      >
        {reveal ? <EyeOff size={14}/> : <Eye size={14}/>}
      </button>
    </div>
  );
}

function ModeSection({ mode, cfg, patch, setPatch, lang }) {
  const L = (pl, en) => (lang === "en" ? en : pl);
  const c = cfg?.[mode] || {};
  const p = patch?.[mode] || {};
  const setField = (field, val) => setPatch((prev) => ({
    ...prev,
    [mode]: { ...(prev[mode] || {}), [field]: val },
  }));
  const setPrice = (lk, val) => setPatch((prev) => ({
    ...prev,
    [mode]: { ...(prev[mode] || {}), prices: { ...((prev[mode] || {}).prices || {}), [lk]: val } },
  }));
  const priceOf = (lk) => (p.prices?.[lk] ?? c.prices?.[lk] ?? "");
  return (
    <div className="paper p-6 space-y-5" data-testid={`stripe-mode-${mode}`}>
      <div className="label-cap">{mode === "test" ? L("Test Mode (sk_test_…)","Test Mode (sk_test_…)") : L("Live Mode (sk_live_…)","Live Mode (sk_live_…)")}</div>

      <div>
        <label className="mono text-[10px] tracking-widest uppercase text-amber-500/80">Publishable Key</label>
        <input
          data-testid={`stripe-${mode}-pk`}
          value={p.publishable_key ?? c.publishable_key ?? ""}
          onChange={(e) => setField("publishable_key", e.target.value)}
          className="script-input mono text-sm mt-2 w-full"
          placeholder={mode === "test" ? "pk_test_..." : "pk_live_..."}
          autoComplete="off"
        />
      </div>

      <div>
        <label className="mono text-[10px] tracking-widest uppercase text-amber-500/80 flex items-center gap-1.5">
          <ShieldCheck size={11}/> Secret Key <span className="text-stone-500 normal-case tracking-normal text-[10px]">({L("nigdy nie wraca do frontend", "never returns to the frontend")})</span>
        </label>
        <div className="mt-2">
          <SecretInput
            testid={`stripe-${mode}-sk`}
            value={p.secret_key ?? ""}
            existingLast4={c.secret_key_last4 || ""}
            hasValue={c.has_secret_key}
            onChange={(v) => setField("secret_key", v)}
            placeholder={mode === "test" ? "sk_test_..." : "sk_live_..."}
          />
        </div>
        <p className="text-stone-500 text-[11px] mt-1 mono">
          {L("Pozostaw puste, aby zachować obecny klucz.", "Leave empty to keep the current key.")}
        </p>
      </div>

      <div>
        <label className="mono text-[10px] tracking-widest uppercase text-amber-500/80 flex items-center gap-1.5">
          <ShieldCheck size={11}/> Webhook Signing Secret
        </label>
        <div className="mt-2">
          <SecretInput
            testid={`stripe-${mode}-whsec`}
            value={p.webhook_secret ?? ""}
            existingLast4={c.webhook_secret_last4 || ""}
            hasValue={c.has_webhook_secret}
            onChange={(v) => setField("webhook_secret", v)}
            placeholder="whsec_..."
          />
        </div>
        <p className="text-stone-500 text-[11px] mt-1 mono">
          {L("Pozostaw puste, aby zachować obecny sekret.", "Leave empty to keep the current secret.")}
        </p>
      </div>

      <div className="pt-2 border-t border-stone-800/60">
        <div className="mono text-[10px] tracking-widest uppercase text-stone-400 mb-3">Price IDs (Stripe → Products → Prices)</div>
        <div className="grid gap-3">
          {PRICE_KEYS.map((pk) => (
            <div key={pk.id} className="grid grid-cols-3 gap-3 items-center">
              <div className="mono text-xs text-stone-300">{pk.i18n}</div>
              <input
                data-testid={`stripe-${mode}-price-${pk.id}`}
                value={priceOf(pk.id)}
                onChange={(e) => setPrice(pk.id, e.target.value)}
                className="script-input mono text-xs col-span-2 w-full"
                placeholder="price_..."
                autoComplete="off"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminStripe() {
  const { user, lang } = useAuth();
  const navigate = useNavigate();
  const t = (k) => tr(k, lang);
  const L = (pl, en) => (lang === "en" ? en : pl);
  const [cfg, setCfg] = useState(null);
  const [patch, setPatch] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!user.is_admin) { navigate("/dashboard"); return; }
    (async () => {
      try {
        const r = await api.get("/admin/stripe/config");
        setCfg(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || L("Błąd wczytywania konfiguracji.", "Failed to load configuration."));
      } finally { setLoading(false); }
    })();
  }, [user, navigate]);

  const currentMode = patch.mode || cfg?.mode || "test";

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...patch };
      // Do not send empty patches for modes with no changes
      MODES.forEach((m) => {
        const p = body[m];
        if (p && Object.keys(p).length === 0) delete body[m];
      });
      const r = await api.post("/admin/stripe/config", body);
      setCfg(r.data);
      setPatch({});
      toast.success(L("Konfiguracja Stripe zapisana.", "Stripe configuration saved."));
    } catch (e) {
      toast.error(e?.response?.data?.detail || L("Nie udało się zapisać.", "Save failed."));
    } finally { setSaving(false); }
  };

  const switchMode = async (m) => {
    try {
      const r = await api.post("/admin/stripe/mode", { mode: m });
      setCfg(r.data);
      setPatch((p) => { const { mode: _, ...rest } = p; return rest; });
      toast.success(L(`Przełączono na ${m === "live" ? "Live Mode" : "Test Mode"}.`, `Switched to ${m === "live" ? "Live Mode" : "Test Mode"}.`));
    } catch (e) {
      toast.error(e?.response?.data?.detail || L("Nie udało się przełączyć trybu.", "Mode switch failed."));
    }
  };

  if (loading) return <div className="text-stone-500 mono text-xs">{L("Ładowanie…", "Loading…")}</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <button onClick={() => navigate(-1)} className="ghost-btn !py-1.5 !text-[10px] mb-3">
              <ChevronLeft size={12}/> {L("Wróć", "Back")}
            </button>
            <h1 className="serif text-3xl text-stone-100">{L("Konfiguracja Stripe", "Stripe Configuration")}</h1>
            <p className="text-stone-400 text-sm mt-1">
              {L("Dane są przechowywane po stronie backendu w kolekcji", "Data is stored on the backend in the collection")} <span className="mono text-stone-300">settings.stripe</span>.
              {L("Sekretny klucz i webhook signing secret nigdy nie wracają do frontendu w postaci jawnej — widać tylko ostatnie 4 znaki.", "The secret key and webhook signing secret never return to the frontend in plaintext — only the last 4 characters are visible.")}
            </p>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="paper p-5 flex items-center justify-between gap-4">
          <div>
            <div className="label-cap">{L("Aktywne środowisko", "Active environment")}</div>
            <div className="serif text-xl text-stone-100 mt-1 flex items-center gap-2">
              <Zap size={16} className={currentMode === "live" ? "text-emerald-400" : "text-amber-400"}/>
              {currentMode === "live" ? "Live Mode" : "Test Mode"}
            </div>
          </div>
          <div className="flex gap-2" data-testid="stripe-mode-switch">
            {MODES.map((m) => (
              <button
                key={m}
                data-testid={`stripe-mode-btn-${m}`}
                onClick={() => switchMode(m)}
                className={`px-4 py-2 rounded-sm mono text-[11px] tracking-widest uppercase transition-colors border ${
                  currentMode === m
                    ? "bg-amber-500 text-stone-950 border-amber-500"
                    : "bg-transparent text-stone-400 border-stone-700 hover:border-amber-500/50 hover:text-amber-400"
                }`}
              >
                {m === "test" ? "Test" : "Live"}
              </button>
            ))}
          </div>
        </div>

        <ModeSection mode="test" cfg={cfg} patch={patch} setPatch={setPatch} lang={lang}/>
        <ModeSection mode="live" cfg={cfg} patch={patch} setPatch={setPatch} lang={lang}/>

        <div className="flex justify-end gap-2 sticky bottom-0 py-4 bg-stone-950/95 backdrop-blur-sm z-10 border-t border-stone-800/60">
          <button
            onClick={() => setPatch({})}
            disabled={saving || Object.keys(patch).length === 0}
            className="ghost-btn"
          >
            {L("Anuluj zmiany", "Cancel changes")}
          </button>
          <button
            data-testid="stripe-save-btn"
            onClick={save}
            disabled={saving || Object.keys(patch).length === 0}
            className="pill-btn"
          >
            {saving ? (<><RefreshCw size={14} className="animate-spin"/> {L("Zapisuję…", "Saving…")}</>) : (<><Save size={14}/> {L("Zapisz zmiany", "Save changes")}</>)}
          </button>
        </div>
      </div>
  );
}
